import { DateTime } from 'luxon';
import z from 'zod';

import { DtiPublicApiError } from '@/core/dtiPublicApi/errors';
import { toLeadInsuranceSelectionPayload } from '@/core/dtiPublicApi/insuranceSelection';
import { err, ok, type Result } from '@/core/dtiPublicApi/result';
import {
  ageGroupCodeSchema,
  ageGroupLabelSchema,
  appointmentIdSchema,
  counselingStyleCodeSchema,
  dtiConfigResponseSchema,
  dtiConfigStepSchema,
  fileAttachmentIdSchema,
  firstNameSchema,
  insuranceCompanyIdSchema,
  insuranceCompanyNameSchema,
  insuranceSearchCodeSchema,
  isoDateSchema,
  isoTimestampSchema,
  lastNameSchema,
  leadIdSchema,
  maskedEmailSchema,
  maskedPhoneSchema,
  membershipIntervalSchema,
  membershipPackageDescriptionSchema,
  membershipPackageIdSchema,
  membershipPackageNameSchema,
  normalizedStateCodeSchema,
  otherCompanyNameSchema,
  partnerConfigSchema,
  promoCodeSchema,
  providerBioSchema,
  providerDisplayNameSchema,
  providerIdSchema,
  publicLeadInsuranceSchema,
  reasonForVisitCodeExactSchema,
  reasonForVisitCodeSchema,
  searchProviderFiltersResponseSchema,
  specialtyCodeSchema,
  unsafeParse,
  urlSchema,
  usPhoneSchema,
  usStateCodeSchema,
} from '@/core/dtiPublicApi/schemas';
import { type Task } from '@/core/dtiPublicApi/task';
import type {
  AppointmentResult,
  Availability,
  BookAppointmentCommand,
  BookAppointmentResult,
  BrowserTimezone,
  ConsumeLeadInviteCommand,
  ConsumeLeadInviteResult,
  ConsumeMagicLinkCommand,
  ConsumeMagicLinkResult,
  CreateLeadCommand,
  CreateLeadResult,
  DtiConfig,
  DtiConfigVariant,
  EligibilityStatus,
  InsuranceCompany,
  InsuranceSelectionInput,
  IsoTimestamp,
  LeadId,
  MaskedEmail,
  MaskedPhone,
  MembershipPackage,
  PartnerConfig,
  ProviderAvailabilityResult,
  ProviderId,
  ProviderSummary,
  PublicLead,
  PublicReasonForVisit,
  ReferenceData,
  RequestMagicLinkCommand,
  RequestMagicLinkResult,
  SavePaymentMethodCommand,
  SavePaymentMethodResult,
  SearchProviderFilters,
  SearchProvidersQuery,
  SearchProvidersResult,
  SelectMembershipPlanCommand,
  SelectMembershipPlanResult,
  SubmitInsuranceCommand,
  SubmitInsuranceResult,
  UpdateLeadCommand,
  UpdateLeadResult,
  UploadInsuranceCardCommand,
  UploadInsuranceCardResult,
  UsPhone,
  UsStateCode,
  ValidatePromoCodeCommand,
  ValidatePromoCodeResult,
} from '@/core/dtiPublicApi/types';
import { widenUsStateCode } from '@/core/dtiPublicApi/types';

// Long enough to keep pending UI observable, short enough not to dominate
// E2E runtime (400ms cost about 23% of the Chromium suite).
const MOCK_LATENCY_MS = 50;
const MOCK_CREATE_LEAD_FIELD_ERROR_EMAIL = 'field-error@example.com';
const MOCK_CREATE_LEAD_GENERAL_ERROR_EMAIL = 'general-error@example.com';
const MOCK_CREATE_LEAD_NETWORK_ERROR_EMAIL = 'network-error@example.com';
const MOCK_CREATE_LEAD_MAGIC_LINK_SENT_EMAIL = 'magic-link-sent@example.com';
const MOCK_UPDATE_LEAD_SENTINEL_EMAIL = 'update-lead-sentinel@example.com';
const MOCK_BOOK_APPOINTMENT_PROVIDER_REJECTED_EMAIL =
  'appointment-provider-rejected@example.com';
const MOCK_SEARCH_AVAILABILITY_FAILED_QUERY = 'availability-failed';
export const MOCK_AGE_INELIGIBLE_DATE_OF_BIRTH = '2000-01-02';
export const MOCK_AGE_INELIGIBLE_MESSAGE =
  'This provider does not see patients in this age group. Please choose another provider.';
export const MOCK_SEARCH_DENSE_AVAILABILITY_QUERY = 'dense-availability';
function isMockAgeIneligibleDateOfBirth(
  dateOfBirth:
    Readonly<{ year: number; month: number; day: number }> | undefined,
): boolean {
  return (
    dateOfBirth?.year === 2000 &&
    dateOfBirth.month === 1 &&
    dateOfBirth.day === 2
  );
}

function mockAgeIneligibleError() {
  return {
    field: ['appointment', 'providerId'],
    messages: [MOCK_AGE_INELIGIBLE_MESSAGE],
    code: 'AGE_INELIGIBLE' as const,
  };
}
// Browser-scenario switches, set before boot by a spec and read at call time:
// the mock fails while the key holds 'true', so a spec exercises the real
// retry by removing the key.
export const MOCK_SEARCH_FAILURE_STORAGE_KEY = 'v3_dti_mock_search_failure';
export const MOCK_INSURANCE_FAILURE_STORAGE_KEY =
  'v3_dti_mock_insurance_failure';
export const MOCK_PROMO_REJECTED_CODE = 'REJECTED';
const MOCK_SUBMIT_INSURANCE_POLICYHOLDER_ERROR_MEMBER_ID =
  'mock-policyholder-error';
// Fixture tokens below are format-valid (see leadInviteTokenSchema /
// magicLinkTokenSchema): the magic-link half is btoa(<label>) plus a zero digest.
export const MOCK_EXPIRED_INVITE_TOKEN = 'expiredinvitetoken123456';
export const MOCK_TEMPORARY_FAILURE_INVITE_TOKEN = 'brokeninvitetoken1234567';
export const MOCK_ALREADY_BOOKED_TOKEN =
  'YWxyZWFkeS1ib29rZWQtdG9rZW4=--0000000000000000000000000000000000000000';
export const MOCK_BOOKED_RECOVERY_TOKEN =
  'Ym9va2VkLXNpZ25lZC10b2tlbg==--0000000000000000000000000000000000000000';
export const MOCK_SELF_PAY_NEEDS_PLAN_TOKEN =
  'c2VsZi1wYXktbmVlZHMtcGxhbi10b2tlbg==--0000000000000000000000000000000000000000';
// Token the mock "emails" when createLead answers magic_link_sent for a
// returning customer. Unlike the fixed fixture tokens above, it is a
// credential the mock issued, so consumeMagicLink honors it once.
export const MOCK_ISSUED_MAGIC_LINK_TOKEN =
  'aXNzdWVkLW1hZ2ljLWxpbmstdG9rZW4=--0000000000000000000000000000000000000000';
// Format-valid credentials the mock consumes as a normal lead; specs import
// these instead of inventing strings.
export const MOCK_MAGIC_LINK_TOKEN =
  'bW9jay1tYWdpYy1saW5rLXRva2Vu--0000000000000000000000000000000000000000';
export const MOCK_LEAD_INVITE_TOKEN = 'mockinvitetoken123456789';

const mockIssuedMagicLinkExpiredResult: ConsumeMagicLinkResult = {
  tag: 'validation_error',
  lead: null,
  errors: [
    { field: ['token'], messages: ['Invalid or expired token'], code: null },
  ],
};

function delayedResult<TValue, TError>(
  result: Result<TValue, TError>,
  options?: { signal?: AbortSignal; abortError?: TError },
): Task<TValue, TError> {
  return async () => {
    const abortError = options?.abortError;
    const signal = options?.signal;
    if (signal?.aborted && abortError !== undefined) {
      return err(abortError);
    }
    await new Promise<void>((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));
    return signal?.aborted && abortError !== undefined
      ? err(abortError)
      : result;
  };
}

const maskEmail = (email: string): MaskedEmail => {
  const atIndex = email.indexOf('@');
  const masked =
    atIndex <= 0
      ? `${email[0]}***@masked.invalid`
      : `${email[0]}***${email.slice(atIndex)}`;
  return unsafeParse(maskedEmailSchema, masked);
};

const maskPhone = (phone: UsPhone): MaskedPhone | null => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return null;
  return unsafeParse(maskedPhoneSchema, `***-***-${digits.slice(-4)}`);
};
const parseOptionalFirstName = (
  value: string | null | undefined,
): PublicLead['firstName'] =>
  value === null || value === undefined
    ? null
    : unsafeParse(firstNameSchema, value);

const parseOptionalLastName = (
  value: string | null | undefined,
): PublicLead['lastName'] =>
  value === null || value === undefined
    ? null
    : unsafeParse(lastNameSchema, value);

const addMinutes = (value: Date, minutes: number): Date =>
  new Date(value.getTime() + minutes * 60_000);

const addDays = (value: Date, days: number): Date =>
  new Date(value.getTime() + days * 24 * 60 * 60_000);

const makeUtcIsoDaysFromNow = (days: number): string =>
  addDays(new Date(), days).toISOString();
const makeUtcIsoDateDaysFromNow = (days: number): string =>
  makeUtcIsoDaysFromNow(days).slice(0, 10);

const makeMockAvailabilityPreview = (days: number) => ({
  slots: [
    {
      startTime: unsafeParse(isoTimestampSchema, makeUtcIsoDaysFromNow(days)),
    },
  ],
});

type MockLead = {
  id: LeadId;
  email: MaskedEmail | null;
  phone?: UsPhone;
  firstName: PublicLead['firstName'];
  lastName: PublicLead['lastName'];
  reasonForVisitCode: PublicLead['reasonForVisitCode'];
  providerId?: ProviderId;
  startTime?: IsoTimestamp;
  state?: UsStateCode | null;
  dateOfBirthOnFile: boolean;
  referralSourceOnFile: boolean;
  consentToContactViaSms?: boolean | null;
  appointmentConsent?: boolean | null;
  selectedMembershipPlanId?: MembershipPackage['id'];
  cardOnFile?: boolean;
  // Mock-only trigger captured from the raw DOB; the stored lead omits the DOB.
  ageIneligible?: true;
  // Mock-only trigger captured from the raw createLead email; the stored
  // email is masked, so a sentinel cannot be matched after creation.
  rejectsAppointmentProvider?: true;
};

const mockInsurances: InsuranceCompany[] = [
  {
    id: unsafeParse(
      insuranceCompanyIdSchema,
      '9f4e0f2b-b2d0-4f35-9d4b-7fd7f6fb2b01',
    ),
    name: 'Aetna',
    insuranceSearchCode: unsafeParse(insuranceSearchCodeSchema, 'AETNA'),
  },
  {
    id: unsafeParse(
      insuranceCompanyIdSchema,
      '4f7d7b7c-1d3c-4dd1-9f8a-9f3ca7e8e901',
    ),
    name: 'Anthem',
    insuranceSearchCode: unsafeParse(insuranceSearchCodeSchema, 'ANTHEM'),
  },
  {
    id: unsafeParse(
      insuranceCompanyIdSchema,
      '0cf2a1d5-4f7d-4d0f-b2db-8f9aa3e0f111',
    ),
    name: 'Blue Cross Blue Shield',
    insuranceSearchCode: unsafeParse(insuranceSearchCodeSchema, 'BCBS'),
  },
  {
    id: unsafeParse(
      insuranceCompanyIdSchema,
      'e5f9f6be-0f2f-4e7c-8f9f-8f3fb4f8e122',
    ),
    name: 'Cigna',
    insuranceSearchCode: unsafeParse(insuranceSearchCodeSchema, 'CIGNA'),
  },
  {
    id: unsafeParse(
      insuranceCompanyIdSchema,
      '7a1c3e9d-2b5f-4c8e-9d1a-3f6b8c2e4a55',
    ),
    name: 'Humana',
    insuranceSearchCode: unsafeParse(insuranceSearchCodeSchema, 'HUMANA'),
  },
  {
    id: unsafeParse(
      insuranceCompanyIdSchema,
      'b8d2f4a6-5c7e-4f9a-8b3d-1e5c7a9f2d66',
    ),
    name: 'Kaiser Permanente',
    insuranceSearchCode: unsafeParse(insuranceSearchCodeSchema, 'KAISER'),
  },
  {
    id: unsafeParse(
      insuranceCompanyIdSchema,
      'c9e3a5b7-6d8f-4a1b-9c4e-2f6d8b1a3e77',
    ),
    name: 'Oscar Health',
    insuranceSearchCode: unsafeParse(insuranceSearchCodeSchema, 'OSCAR'),
  },
  {
    id: unsafeParse(
      insuranceCompanyIdSchema,
      'd1f4b6c8-7e9a-4b2c-8d5f-3a7e9c2b4f88',
    ),
    name: 'UnitedHealthcare',
    insuranceSearchCode: unsafeParse(insuranceSearchCodeSchema, 'UHC'),
  },
];
const CLOSEKNIT_PARTNER_CONFIG = unsafeParse(partnerConfigSchema, {
  name: 'CloseKnit',
  code: 'closeknit',
  brandLogoUrl: null,
  creditCardRequired: false,
  insuranceRequired: true,
  codeEntryEnabled: true,
  ageGroup: 'adult',
  restrictInsurers: true,
  eligibleInsurers: [mockInsurances[0]!],
});
const MALFORMED_RESTRICTED_PARTNER_CONFIG = unsafeParse(partnerConfigSchema, {
  name: 'Malformed Restricted',
  code: 'malformed-restricted-insurer',
  brandLogoUrl: null,
  creditCardRequired: true,
  insuranceRequired: true,
  codeEntryEnabled: false,
  ageGroup: 'adult',
  restrictInsurers: true,
  eligibleInsurers: [],
});
// A partner that gates nothing: no access code, no insurance requirement, no
// insurer restriction. Every other partner fixture here is code-gated or
// deliberately malformed, which is unrepresentative of production and hides
// regressions in the ordinary partner path.
const RIVERSIDE_PARTNER_CONFIG = unsafeParse(partnerConfigSchema, {
  name: 'Riverside Health',
  code: 'riverside',
  brandLogoUrl: null,
  creditCardRequired: false,
  insuranceRequired: false,
  codeEntryEnabled: false,
  ageGroup: 'adult',
  restrictInsurers: false,
  eligibleInsurers: [],
});

const MOCK_BOOK_APPOINTMENT_STEP = unsafeParse(dtiConfigStepSchema, {
  name: 'booking',
  order: 1,
  required: true,
  condition: null,
  queries: [],
  actions: [
    {
      name: 'bookAppointment',
      fields: ['providerId', 'startTime'],
    },
  ],
  fields: [
    { name: 'providerId', required: true, order: 1 },
    { name: 'startTime', required: true, order: 2 },
  ],
});
const CLOSEKNIT_ALLOWED_PROVIDER_IDS: Record<string, true> = {
  'provider-1': true,
};

const mockProviders: ProviderSummary[] = [
  {
    id: unsafeParse(providerIdSchema, 'provider-1'),
    firstName: unsafeParse(providerDisplayNameSchema, 'Avery'),
    lastName: unsafeParse(providerDisplayNameSchema, 'Miles'),
    bio: unsafeParse(
      providerBioSchema,
      'Specializing in diabetes-focused behavior change and practical nutrition planning with an emphasis on sustainable routines, flexible meal frameworks, and supportive accountability for busy adults balancing work, family, and health goals.',
    ),
    photoUrl: unsafeParse(
      urlSchema,
      'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=300&h=300&fit=crop&auto=format',
    ),
    states: [
      unsafeParse(normalizedStateCodeSchema, 'NY'),
      unsafeParse(normalizedStateCodeSchema, 'NJ'),
      unsafeParse(normalizedStateCodeSchema, 'CT'),
    ],
    reasonsForVisit: [
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_SUSTAINABLE_WEIGHT_LOSS'),
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_TYPE_2_DIABETES'),
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_PREDIABETES'),
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_TYPE_1_DIABETES'),
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_GESTATIONAL_DIABETES'),
    ],
    nextAvailableSlot: unsafeParse(
      isoTimestampSchema,
      makeUtcIsoDaysFromNow(1),
    ),
    nextAvailableDate: unsafeParse(isoDateSchema, makeUtcIsoDateDaysFromNow(1)),
    availabilityPreview: makeMockAvailabilityPreview(1),
    specialties: [
      {
        code: unsafeParse(specialtyCodeSchema, 'weight_management'),
        name: 'Weight Management',
      },
      { code: unsafeParse(specialtyCodeSchema, 'diabetes'), name: 'Diabetes' },
    ],
    counselingStyles: [
      {
        code: unsafeParse(counselingStyleCodeSchema, 'COLLABORATIVE'),
        name: 'Gentle',
      },
      {
        code: unsafeParse(counselingStyleCodeSchema, 'PRESCRIPTIVE'),
        name: 'Direct',
      },
    ],
    ageGroups: [
      {
        code: unsafeParse(ageGroupCodeSchema, 'AG_ADULT'),
        name: unsafeParse(ageGroupLabelSchema, '18+ years'),
        label: unsafeParse(ageGroupLabelSchema, 'Adult (18+ years)'),
      },
    ],
    acceptingNewPatients: true,
  },
  {
    id: unsafeParse(providerIdSchema, 'provider-4'),
    firstName: unsafeParse(providerDisplayNameSchema, 'Morgan'),
    lastName: unsafeParse(providerDisplayNameSchema, 'Lee'),
    bio: unsafeParse(
      providerBioSchema,
      'Helping New Yorkers navigate gut health and food sensitivities with evidence-based nutrition.',
    ),
    photoUrl: unsafeParse(
      urlSchema,
      'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=300&h=300&fit=crop&auto=format',
    ),
    states: [
      unsafeParse(normalizedStateCodeSchema, 'NY'),
      unsafeParse(normalizedStateCodeSchema, 'CT'),
    ],
    reasonsForVisit: [
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_GASTROINTESTINAL_HEALTH'),
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_SUSTAINABLE_WEIGHT_LOSS'),
    ],
    // The cursor-paged e2e fixture: no preview and no next-slot pointer, so the
    // populated-preview-whenever-next-slot invariant holds and the browse flow
    // exercises the fetch path via the merged date control.
    nextAvailableSlot: null,
    nextAvailableDate: null,
    availabilityPreview: null,
    specialties: [
      {
        code: unsafeParse(specialtyCodeSchema, 'gi_health'),
        name: 'GI Health',
      },
    ],
    counselingStyles: [
      {
        code: unsafeParse(counselingStyleCodeSchema, 'COLLABORATIVE'),
        name: 'Gentle / Collaborative',
      },
    ],
    ageGroups: [
      {
        code: unsafeParse(ageGroupCodeSchema, 'AG_ADULT'),
        name: unsafeParse(ageGroupLabelSchema, '18+ years'),
        label: unsafeParse(ageGroupLabelSchema, 'Adult (18+ years)'),
      },
    ],
    acceptingNewPatients: true,
  },
  {
    id: unsafeParse(providerIdSchema, 'provider-5'),
    firstName: unsafeParse(providerDisplayNameSchema, 'Riley'),
    lastName: unsafeParse(providerDisplayNameSchema, 'Chen'),
    bio: unsafeParse(
      providerBioSchema,
      'Specializing in plant-based eating and sports nutrition for active adults in the Northeast.',
    ),
    photoUrl: null,
    states: [
      unsafeParse(normalizedStateCodeSchema, 'NY'),
      unsafeParse(normalizedStateCodeSchema, 'NJ'),
    ],
    reasonsForVisit: [
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_HEART_HEALTH'),
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_SUSTAINABLE_WEIGHT_LOSS'),
    ],
    nextAvailableSlot: unsafeParse(
      isoTimestampSchema,
      makeUtcIsoDaysFromNow(4),
    ),
    nextAvailableDate: unsafeParse(isoDateSchema, makeUtcIsoDateDaysFromNow(4)),
    availabilityPreview: makeMockAvailabilityPreview(4),
    specialties: [
      {
        code: unsafeParse(specialtyCodeSchema, 'heart_health'),
        name: 'Heart Health',
      },
    ],
    counselingStyles: [
      {
        code: unsafeParse(counselingStyleCodeSchema, 'PRESCRIPTIVE'),
        name: 'Direct / Prescriptive',
      },
    ],
    ageGroups: [
      {
        code: unsafeParse(ageGroupCodeSchema, 'AG_ADULT'),
        name: unsafeParse(ageGroupLabelSchema, '18+ years'),
        label: unsafeParse(ageGroupLabelSchema, 'Adult (18+ years)'),
      },
    ],
    acceptingNewPatients: true,
  },
  {
    id: unsafeParse(providerIdSchema, 'provider-2'),
    firstName: unsafeParse(providerDisplayNameSchema, 'Jordan'),
    lastName: unsafeParse(providerDisplayNameSchema, 'Shah'),
    bio: unsafeParse(
      providerBioSchema,
      'Focused on PCOS and hormone balancing through nutrition coaching.',
    ),
    photoUrl: unsafeParse(
      urlSchema,
      'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=300&h=300&fit=crop&auto=format',
    ),
    states: [
      unsafeParse(normalizedStateCodeSchema, 'CA'),
      unsafeParse(normalizedStateCodeSchema, 'NV'),
    ],
    reasonsForVisit: [
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_HORMONE_HEALTH'),
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_HEART_HEALTH'),
    ],
    nextAvailableSlot: unsafeParse(
      isoTimestampSchema,
      makeUtcIsoDaysFromNow(2),
    ),
    nextAvailableDate: unsafeParse(isoDateSchema, makeUtcIsoDateDaysFromNow(2)),
    availabilityPreview: makeMockAvailabilityPreview(2),
    specialties: [
      { code: unsafeParse(specialtyCodeSchema, 'pcos'), name: 'PCOS' },
      {
        code: unsafeParse(specialtyCodeSchema, 'heart_health'),
        name: 'Heart Health',
      },
    ],
    counselingStyles: [
      {
        code: unsafeParse(counselingStyleCodeSchema, 'COLLABORATIVE'),
        name: 'Gentle / Collaborative',
      },
    ],
    ageGroups: [
      {
        code: unsafeParse(ageGroupCodeSchema, 'AG_ADULT'),
        name: unsafeParse(ageGroupLabelSchema, '18+ years'),
        label: unsafeParse(ageGroupLabelSchema, 'Adult (18+ years)'),
      },
    ],
    acceptingNewPatients: true,
  },
  {
    id: unsafeParse(providerIdSchema, 'provider-3'),
    firstName: unsafeParse(providerDisplayNameSchema, 'Sam'),
    lastName: unsafeParse(providerDisplayNameSchema, 'Nguyen'),
    bio: unsafeParse(
      providerBioSchema,
      'Offers short-term habit reset frameworks and stress-sensitive meal planning.',
    ),
    photoUrl: unsafeParse(
      urlSchema,
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=300&h=300&fit=crop&auto=format',
    ),
    states: [
      unsafeParse(normalizedStateCodeSchema, 'TX'),
      unsafeParse(normalizedStateCodeSchema, 'CA'),
      unsafeParse(normalizedStateCodeSchema, 'AZ'),
    ],
    reasonsForVisit: [
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_EATING_DISORDERS'),
      unsafeParse(reasonForVisitCodeExactSchema, 'RFV_SUSTAINABLE_WEIGHT_LOSS'),
    ],
    nextAvailableSlot: null,
    nextAvailableDate: null,
    availabilityPreview: null,
    specialties: [
      {
        code: unsafeParse(specialtyCodeSchema, 'eating_disorders'),
        name: 'Eating Disorders',
      },
      {
        code: unsafeParse(specialtyCodeSchema, 'weight_management'),
        name: 'Weight Management',
      },
    ],
    counselingStyles: [
      {
        code: unsafeParse(counselingStyleCodeSchema, 'PRESCRIPTIVE'),
        name: 'Direct / Prescriptive',
      },
    ],
    ageGroups: [
      {
        code: unsafeParse(ageGroupCodeSchema, 'AG_ADULT'),
        name: unsafeParse(ageGroupLabelSchema, '18+ years'),
        label: unsafeParse(ageGroupLabelSchema, 'Adult (18+ years)'),
      },
      {
        code: unsafeParse(ageGroupCodeSchema, 'AG_LATE_TEEN'),
        name: unsafeParse(ageGroupLabelSchema, '16-17 years'),
        label: unsafeParse(ageGroupLabelSchema, 'Late Teen (16-17 years)'),
      },
    ],
    acceptingNewPatients: false,
  },
];

const mockReasonsForVisit: PublicReasonForVisit[] = [
  { code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_ANY'), name: 'Any' },
  {
    code: unsafeParse(
      reasonForVisitCodeExactSchema,
      'RFV_SUSTAINABLE_WEIGHT_LOSS',
    ),
    name: 'Sustainable Weight Loss',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_HEART_HEALTH'),
    name: 'Heart Health',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_PREDIABETES'),
    name: 'Prediabetes',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_TYPE_2_DIABETES'),
    name: 'Type 2 Diabetes',
  },
  {
    code: unsafeParse(
      reasonForVisitCodeExactSchema,
      'RFV_GASTROINTESTINAL_HEALTH',
    ),
    name: 'Gastrointestinal Health',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_OTHER'),
    name: 'Other',
  },
  {
    code: unsafeParse(
      reasonForVisitCodeExactSchema,
      'RFV_GENERAL_HEALTHY_EATING',
    ),
    name: 'General Healthy Eating',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_EATING_DISORDERS'),
    name: 'Eating Disorders',
  },
  {
    code: unsafeParse(
      reasonForVisitCodeExactSchema,
      'RFV_RELATIONSHIP_WITH_FOOD',
    ),
    name: 'Relationship with Food',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_TYPE_1_DIABETES'),
    name: 'Type 1 Diabetes',
  },
  {
    code: unsafeParse(
      reasonForVisitCodeExactSchema,
      'RFV_GESTATIONAL_DIABETES',
    ),
    name: 'Gestational Diabetes',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_HORMONE_HEALTH'),
    name: 'Hormone Health',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_FERTILITY_NUTRITION'),
    name: 'Fertility Nutrition',
  },
  {
    code: unsafeParse(
      reasonForVisitCodeExactSchema,
      'RFV_CANCER_CARE_NUTRITION',
    ),
    name: 'Cancer Care Nutrition',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_SPORTS_NUTRITION'),
    name: 'Sports Nutrition',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_FOOD_ALLERGIES'),
    name: 'Food Allergies',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_THYROID_CONDITION'),
    name: 'Thyroid Condition',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_PEDIATRICS'),
    name: 'Pediatrics',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_KIDNEY_LIVER_HEALTH'),
    name: 'Kidney & Liver Health',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_WEIGHT_MANAGEMENT'),
    name: 'Weight Management',
  },
  {
    code: unsafeParse(reasonForVisitCodeExactSchema, 'RFV_GLP1_CARE'),
    name: 'GLP-1 Care',
  },
];

export const mockMembershipPackages: MembershipPackage[] = [
  {
    id: unsafeParse(
      membershipPackageIdSchema,
      '019ddf80-880a-790d-a107-8a0166090b8a',
    ),
    name: unsafeParse(membershipPackageNameSchema, 'Essentials'),
    priceCents: 9900,
    description: unsafeParse(
      membershipPackageDescriptionSchema,
      'Ongoing support for a low monthly fee',
    ),
    interval: unsafeParse(membershipIntervalSchema, 'monthly'),
  },
  {
    id: unsafeParse(
      membershipPackageIdSchema,
      '019ddf80-885a-75fa-b910-0392f9c89cb0',
    ),
    name: unsafeParse(membershipPackageNameSchema, 'Core'),
    priceCents: 16900,
    description: unsafeParse(
      membershipPackageDescriptionSchema,
      'Regular support to see consistent progress',
    ),
    interval: unsafeParse(membershipIntervalSchema, 'monthly'),
  },
  {
    id: unsafeParse(
      membershipPackageIdSchema,
      '019ddf80-886e-7cf3-9978-020ca7daac6c',
    ),
    name: unsafeParse(membershipPackageNameSchema, 'Enhanced'),
    priceCents: 22900,
    description: unsafeParse(
      membershipPackageDescriptionSchema,
      'Extra support for complex health conditions',
    ),
    interval: unsafeParse(membershipIntervalSchema, 'monthly'),
  },
  {
    id: unsafeParse(
      membershipPackageIdSchema,
      '019ddf80-87a0-745a-8633-b66ec0e0e5ba',
    ),
    name: unsafeParse(membershipPackageNameSchema, 'Initial Session'),
    priceCents: 22500,
    description: null,
    interval: unsafeParse(membershipIntervalSchema, 'one_time'),
  },
  {
    id: unsafeParse(
      membershipPackageIdSchema,
      '019ddf80-87f2-7649-9b02-abafdbc34ac8',
    ),
    name: unsafeParse(membershipPackageNameSchema, '6 Session Package'),
    priceCents: 84000,
    description: null,
    interval: unsafeParse(membershipIntervalSchema, 'one_time'),
  },
];

const mockLeadStore = new Map<string, MockLead>();
// Mock transport models a single in-memory browser session; createLead sets the
// active lead used by follow-up calls until resetMockState is invoked.
let activeMockLeadId: LeadId | null = null;

let mockLeadCounter = 1_000;

let mockMagicLinkConsumeOverride: ConsumeMagicLinkResult | null = null;
let mockLeadInviteConsumeOverride: ConsumeLeadInviteResult | null = null;
// One-time tracking for MOCK_ISSUED_MAGIC_LINK_TOKEN: consumed on an earlier
// page load (persisted below) or by a consume that has already completed,
// while a duplicate consume still in flight is the same activation replayed
// by the client (React StrictMode dev double-effect) and stays idempotent.
// null until first read: hydrated from the persisted flag so a page reload
// keeps the token burned, yet re-issuance can write it back to false.
let mockIssuedMagicLinkConsumed: boolean | null = null;
let mockIssuedMagicLinkConsumeInFlight = false;

function isIssuedMagicLinkConsumed(): boolean {
  mockIssuedMagicLinkConsumed ??=
    readMockRecoveryStorage()?.consumedIssuedMagicLinkToken ?? false;
  return mockIssuedMagicLinkConsumed;
}

/**
 * The mock "emails" MOCK_ISSUED_MAGIC_LINK_TOKEN again: it is consumable once
 * more. Patches only the flag: a full persist on a fresh page load would
 * serialize the not-yet-rehydrated (empty) in-memory recovery entries.
 */
function reissueMagicLink(): void {
  mockIssuedMagicLinkConsumed = false;
  const stored = readMockRecoveryStorage();
  if (stored === null) return;
  try {
    window.sessionStorage.setItem(
      MOCK_RECOVERY_STORAGE_KEY,
      JSON.stringify({ ...stored, consumedIssuedMagicLinkToken: false }),
    );
  } catch {
    // Best effort, as with persistMockRecoveryState.
  }
}
const MOCK_BOOKING_FAILURE_MODE_9AM_UTC = 'schedule_failed_9am_utc';
type MockRecoveryEntry = {
  appointment: NonNullable<PublicLead['appointment']> | null;
  insurance: NonNullable<PublicLead['insurance']> | null;
};
const MOCK_RECOVERY_STORAGE_KEY = 'v3_dti_mock_recovery_v1';
const mockRecoveryAppointmentSchema = z.object({
  booked: z.literal(true),
});
/**
 * The active mock lead's own fields, persisted so a full page load (which
 * resets module state) can rebuild what the real API would still know: its
 * self-pay plan, patient details on file, and state. Field names match
 * `MockLead` so the record spreads straight back into `makeMockLead`.
 */
const mockActiveLeadProjectionSchema = z.object({
  email: maskedEmailSchema.nullable(),
  phone: usPhoneSchema.optional(),
  firstName: firstNameSchema.nullable(),
  lastName: lastNameSchema.nullable(),
  state: usStateCodeSchema.nullable().optional(),
  dateOfBirthOnFile: z.boolean(),
  ageIneligible: z.literal(true).optional(),
  consentToContactViaSms: z.boolean().nullable().optional(),
  selectedMembershipPlanId: membershipPackageIdSchema.optional(),
});
const mockRecoveryState = new Map<LeadId, MockRecoveryEntry>();
const mockRecoveryStorageSchema = z.object({
  version: z.literal(1),
  activeLeadId: leadIdSchema.nullable(),
  activeLeadRouteFacts: z
    .object({
      providerId: providerIdSchema.nullable(),
      startTime: isoTimestampSchema.nullable(),
    })
    .nullable(),
  activeLeadProjection: mockActiveLeadProjectionSchema.nullable().optional(),
  entries: z.array(
    z.object({
      leadId: leadIdSchema,
      appointment: mockRecoveryAppointmentSchema.nullable(),
      insurance: publicLeadInsuranceSchema.nullable(),
      appointmentConsent: z.boolean().nullable().default(null),
    }),
  ),
  consumedIssuedMagicLinkToken: z.boolean().optional(),
});

function projectActiveLeadForRecovery(
  activeLead: MockLead,
): z.infer<typeof mockActiveLeadProjectionSchema> {
  const {
    email,
    phone,
    firstName,
    lastName,
    state,
    dateOfBirthOnFile,
    ageIneligible,
    consentToContactViaSms,
    selectedMembershipPlanId,
  } = activeLead;
  return {
    email,
    phone,
    firstName,
    lastName,
    state,
    dateOfBirthOnFile,
    ...(ageIneligible === true ? { ageIneligible: true } : {}),
    consentToContactViaSms,
    selectedMembershipPlanId,
  };
}

function readMockRecoveryStorage(): z.infer<
  typeof mockRecoveryStorageSchema
> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(MOCK_RECOVERY_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = mockRecoveryStorageSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

// `undefined` = no persisted entry; `null` = entry with consent never given.
function readPersistedAppointmentConsent(
  leadId: LeadId,
): boolean | null | undefined {
  return readMockRecoveryStorage()?.entries.find(
    (entry) => entry.leadId === leadId,
  )?.appointmentConsent;
}

function readPersistedRecovery(leadId: LeadId): MockRecoveryEntry | null {
  const recoveryStorage = readMockRecoveryStorage();
  const stored = recoveryStorage?.entries.find(
    (entry) => entry.leadId === leadId,
  );
  if (stored === undefined) return null;
  const routeFacts = recoveryStorage?.activeLeadRouteFacts ?? null;
  const appointment =
    stored.appointment === null ||
    recoveryStorage?.activeLeadId !== leadId ||
    routeFacts?.providerId === null ||
    routeFacts?.providerId === undefined ||
    routeFacts.startTime === null
      ? null
      : makeBookedAppointmentProjection(
          leadId,
          routeFacts.providerId,
          routeFacts.startTime,
        ).publicLeadAppointment;
  return {
    appointment,
    insurance: stored.insurance,
  };
}

function persistMockRecoveryState(): void {
  if (typeof window === 'undefined') return;
  const entries = Array.from(mockRecoveryState, ([leadId, recovery]) => ({
    leadId,
    appointment:
      recovery.appointment === null ? null : { booked: true as const },
    insurance: recovery.insurance,
    appointmentConsent: mockLeadStore.get(leadId)?.appointmentConsent ?? null,
  }));
  const activeLead =
    activeMockLeadId === null ? undefined : mockLeadStore.get(activeMockLeadId);
  try {
    window.sessionStorage.setItem(
      MOCK_RECOVERY_STORAGE_KEY,
      JSON.stringify({
        version: 1,
        activeLeadId: activeMockLeadId,
        activeLeadRouteFacts:
          activeLead === undefined
            ? null
            : {
                providerId: activeLead.providerId ?? null,
                startTime: activeLead.startTime ?? null,
              },
        activeLeadProjection:
          activeLead === undefined
            ? null
            : projectActiveLeadForRecovery(activeLead),
        entries,
        consumedIssuedMagicLinkToken: isIssuedMagicLinkConsumed(),
      }),
    );
  } catch {
    // Mock recovery persistence is best effort, matching browser storage.
  }
}

function getOrCreateRecovery(leadId: LeadId): MockRecoveryEntry {
  let entry = mockRecoveryState.get(leadId);
  if (!entry) {
    entry = readPersistedRecovery(leadId) ?? {
      appointment: null,
      insurance: null,
    };
    mockRecoveryState.set(leadId, entry);
  }
  return entry;
}

function makeBookedAppointmentProjection(
  leadId: LeadId,
  providerId: ProviderId,
  startTime: IsoTimestamp,
): {
  appointment: AppointmentResult;
  publicLeadAppointment: NonNullable<PublicLead['appointment']>;
} {
  const endTime = unsafeParse(
    isoTimestampSchema,
    addMinutes(new Date(startTime), 50).toISOString(),
  );
  const provider = mockProviders.find(
    (candidate) => candidate.id === providerId,
  );
  const providerName = unsafeParse(
    providerDisplayNameSchema,
    [provider?.firstName, provider?.lastName].filter(Boolean).join(' ') ||
      'Provider',
  );
  const joinUrl = unsafeParse(
    urlSchema,
    'https://video.example.com/room/example',
  );
  const appointment: AppointmentResult = {
    id: unsafeParse(appointmentIdSchema, `appt-${leadId}`),
    providerId,
    startTime,
    joinUrl,
    endTime,
    providerName,
    patientTimezone: 'UTC',
  };

  return {
    appointment,
    publicLeadAppointment: {
      id: appointment.id,
      providerId,
      startTime,
      joinUrl,
      endTime,
      providerName,
      providerPhotoUrl: provider?.photoUrl ?? null,
      patientTimezone: 'UTC',
    },
  };
}

export function resetMockState() {
  mockLeadStore.clear();
  mockRecoveryState.clear();
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(MOCK_RECOVERY_STORAGE_KEY);
  }
  activeMockLeadId = null;
  mockLeadCounter = 1_000;
  mockMagicLinkConsumeOverride = null;
  mockLeadInviteConsumeOverride = null;
  mockIssuedMagicLinkConsumed = false;
  mockIssuedMagicLinkConsumeInFlight = false;
  forceOutOfNetworkOnNextSubmit = false;
}
// ponytail: test-only hook, remove when real API replaces mocks
export function seedMockPendingRecovery(startTime?: IsoTimestamp): void {
  const lead = ensureActiveMockLead();
  lead.state = unsafeParse(usStateCodeSchema, 'NY');
  lead.providerId = unsafeParse(providerIdSchema, 'provider-1');
  lead.startTime =
    startTime ??
    unsafeParse(isoTimestampSchema, addDays(new Date(), 2).toISOString());
  lead.selectedMembershipPlanId = mockMembershipPackages[0]!.id;
}

// ponytail: test-only hook, remove when real API replaces mocks
export function seedMockOtherCarrierRecovery(startTime?: IsoTimestamp): void {
  const lead = ensureActiveMockLead();
  lead.state = unsafeParse(usStateCodeSchema, 'NY');
  lead.providerId = unsafeParse(providerIdSchema, 'provider-1');
  lead.startTime =
    startTime ??
    unsafeParse(isoTimestampSchema, addDays(new Date(), 2).toISOString());

  const insurance = {
    selection: {
      type: 'OTHER' as const,
      networkStatus: 'OUT_OF_NETWORK' as const,
      sponsorId: null,
      otherCompanyName: unsafeParse(otherCompanyNameSchema, 'Acme Benefits'),
    },
    details: {
      memberIdOnFile: true,
      groupNumberOnFile: false,
      subscriberRelationship: 'SELF' as const,
      policyholderOnFile: true,
    },
    eligibilityStatus: 'ACTIVE' as EligibilityStatus,
    cards: null,
    companyName: unsafeParse(insuranceCompanyNameSchema, 'Acme Benefits'),
  } satisfies NonNullable<PublicLead['insurance']>;
  const publicLead = { ...makePublicLead(lead), insurance };
  mockMagicLinkConsumeOverride = { tag: 'consumed', lead: publicLead };
  mockRecoveryState.set(lead.id, {
    appointment: null,
    insurance,
  });
  persistMockRecoveryState();
}

// ponytail: test-only hook, remove when real API replaces mocks
export function seedMockBookedRecovery(startTime?: IsoTimestamp): void {
  const lead = ensureActiveMockLead();
  lead.state = unsafeParse(usStateCodeSchema, 'NY');
  lead.providerId = unsafeParse(providerIdSchema, 'provider-4');
  const bookingStartTime: IsoTimestamp =
    startTime ??
    unsafeParse(isoTimestampSchema, addDays(new Date(), 2).toISOString());
  lead.startTime = bookingStartTime;
  // The link was issued mid-booking; fetchPublicLead reveals the confirmed
  // truth so resume goes through booking_wait -> already booked.
  mockMagicLinkConsumeOverride = {
    tag: 'consumed',
    lead: { ...makePublicLead(lead), bookingStatus: 'IN_PROGRESS' },
  };

  const recoveredProviderId = unsafeParse(providerIdSchema, 'provider-1');
  lead.providerId = recoveredProviderId;
  const aetna = mockInsurances[0]!;
  const { publicLeadAppointment } = makeBookedAppointmentProjection(
    lead.id,
    recoveredProviderId,
    bookingStartTime,
  );
  mockRecoveryState.set(lead.id, {
    appointment: publicLeadAppointment,
    // ponytail: seeds Aetna as default carrier, extend params if other carriers needed
    insurance: {
      selection: {
        type: 'SPONSOR',
        networkStatus: 'IN_NETWORK',
        sponsorId: aetna.id,
        otherCompanyName: null,
      },
      details: {
        memberIdOnFile: false,
        groupNumberOnFile: false,
        subscriberRelationship: 'SELF',
        policyholderOnFile: true,
      },
      eligibilityStatus: 'ACTIVE' as EligibilityStatus,
      cards: null,
      companyName: unsafeParse(insuranceCompanyNameSchema, aetna.name),
    },
  });
  persistMockRecoveryState();
}

// ponytail: test-only hook, remove when real API replaces mocks
export function seedMockAlreadyBookedRecovery(): void {
  mockMagicLinkConsumeOverride = {
    tag: 'already_booked',
    errors: [],
  };
}

// ponytail: test-only hook, remove when real API replaces mocks
export function seedMockExpiredTokenRecovery(): void {
  mockMagicLinkConsumeOverride = {
    tag: 'validation_error',
    lead: null,
    errors: [
      { field: ['token'], messages: ['Invalid or expired token'], code: null },
    ],
  };
}
// ponytail: test-only hook, remove when real API replaces mocks
export function seedMockExpiredInviteRecovery(): void {
  mockLeadInviteConsumeOverride = {
    tag: 'validation_error',
    lead: null,
    errors: [
      { field: ['token'], messages: ['Invalid or expired token'], code: null },
    ],
  };
}

function makeLeadId(): LeadId {
  mockLeadCounter += 1;
  return unsafeParse(leadIdSchema, `lead-${mockLeadCounter}`);
}

function shouldFailMockBookingForStartTime(startTime: string): boolean {
  return (
    process.env.NEXT_PUBLIC_DTI_PUBLIC_MOCK_BOOKING_FAILURE_MODE ===
      MOCK_BOOKING_FAILURE_MODE_9AM_UTC && startTime.endsWith('09:00:00.000Z')
  );
}

function makeAppointmentSlotsFrom(
  intervalStart: DateTime,
  startDay: number,
  timezone: string,
): Availability[] {
  const base = intervalStart
    .setZone(timezone)
    .startOf('day')
    .plus({ days: startDay })
    .set({ hour: 9, minute: 0, second: 0, millisecond: 0 });
  const slots: Availability[] = [];
  for (let i = 0; i < 5; i += 1) {
    const start = base.plus({ hours: i * 2 }).toUTC();
    slots.push({
      startTime: unsafeParse(isoTimestampSchema, start.toISO()!),
    });
  }
  return slots;
}

const PAGED_AVAILABILITY_PROVIDER_ID = unsafeParse(
  providerIdSchema,
  'provider-4',
);
const PAGED_AVAILABILITY_CURSOR = unsafeParse(
  isoTimestampSchema,
  makeUtcIsoDaysFromNow(8),
);
const PAGED_AVAILABILITY_NEXT_CURSOR = unsafeParse(
  isoTimestampSchema,
  makeUtcIsoDaysFromNow(16),
);

const FIXED_NEXT_AVAILABILITY_PROVIDER_ID = unsafeParse(
  providerIdSchema,
  'provider-5',
);
const FIXED_NEXT_AVAILABILITY_DAYS = 4;

function makeOrdinaryAvailability(
  intervalStart: DateTime,
  providerId: ProviderId,
  timezone: string,
  startOffsetDays: 0 | 1,
): Availability[] {
  // provider-5 (Riley Chen) advertises its next availability several days out
  // with nothing sooner. Model that as a fixed day-4 slot so the search
  // preview, nextAvailableDate, and the post-jump fetch all describe the same
  // availability; the requested date window hides it from nearer filters
  // (e.g. Tomorrow) yet surfaces it once the jump targets that date.
  if (providerId === FIXED_NEXT_AVAILABILITY_PROVIDER_ID) {
    const nextAvailableDay = DateTime.now()
      .setZone(timezone)
      .startOf('day')
      .plus({ days: FIXED_NEXT_AVAILABILITY_DAYS });
    // Honor the 14-day request window: the fixed slot only surfaces for
    // intervals that actually contain it, so a request past the slot returns
    // nothing (matching the real fetch contract) rather than a stale earlier
    // appointment.
    const windowStart = intervalStart.startOf('day');
    const windowEnd = windowStart.plus({ days: 14 });
    if (nextAvailableDay < windowStart || nextAvailableDay >= windowEnd) {
      return [];
    }
    return makeAppointmentSlotsFrom(nextAvailableDay, 0, timezone);
  }
  const startDaysByProvider: Record<string, number[]> = {
    'provider-1': [startOffsetDays, startOffsetDays + 2],
    'provider-2': [startOffsetDays],
    'provider-3': [],
  };
  const startDays = startDaysByProvider[providerId] ?? [];
  return startDays.flatMap((startDay) =>
    makeAppointmentSlotsFrom(intervalStart, startDay, timezone),
  );
}

function makeMockLead(overrides?: Partial<MockLead>): MockLead {
  const leadId = makeLeadId();
  return {
    id: leadId,
    email: maskEmail('mock-patient@example.com'),
    firstName: unsafeParse(firstNameSchema, 'Mock'),
    lastName: unsafeParse(lastNameSchema, 'Patient'),
    reasonForVisitCode: unsafeParse(
      reasonForVisitCodeSchema,
      'RFV_SUSTAINABLE_WEIGHT_LOSS',
    ),
    state: undefined,
    dateOfBirthOnFile: false,
    referralSourceOnFile: false,
    ...overrides,
  };
}

function ensureActiveMockLead(): MockLead {
  const existing = findActiveMockLead();
  if (existing !== undefined) return existing;

  const lead = makeMockLead();
  mockLeadStore.set(lead.id, lead);
  activeMockLeadId = lead.id;
  return lead;
}

function findActiveMockLead(): MockLead | undefined {
  const persistedStorage = readMockRecoveryStorage();
  const persistedLeadId = persistedStorage?.activeLeadId ?? null;
  const routeFacts = persistedStorage?.activeLeadRouteFacts ?? null;
  if (persistedLeadId === null && activeMockLeadId !== null) {
    return mockLeadStore.get(activeMockLeadId);
  }
  if (persistedLeadId === null) return undefined;
  const persistedLocalLead = mockLeadStore.get(persistedLeadId);
  const persistedConsent = readPersistedAppointmentConsent(persistedLeadId);
  if (persistedLocalLead !== undefined) {
    const restoredLead = {
      ...persistedLocalLead,
      ...(routeFacts?.providerId === null ||
      routeFacts?.providerId === undefined
        ? {}
        : { providerId: routeFacts.providerId }),
      ...(routeFacts?.startTime === null || routeFacts?.startTime === undefined
        ? {}
        : { startTime: routeFacts.startTime }),
      appointmentConsent:
        persistedConsent === undefined
          ? (persistedLocalLead.appointmentConsent ?? null)
          : persistedConsent,
    };
    mockLeadStore.set(persistedLeadId, restoredLead);
    activeMockLeadId = persistedLeadId;
    return restoredLead;
  }
  const numericId = Number(String(persistedLeadId).replace('lead-', ''));
  if (Number.isSafeInteger(numericId)) {
    mockLeadCounter = Math.max(mockLeadCounter, numericId - 1);
  }
  const lead = makeMockLead({
    id: persistedLeadId,
    ...persistedStorage?.activeLeadProjection,
    ...(routeFacts?.providerId === null || routeFacts?.providerId === undefined
      ? {}
      : { providerId: routeFacts.providerId }),
    ...(routeFacts?.startTime === null || routeFacts?.startTime === undefined
      ? {}
      : { startTime: routeFacts.startTime }),
    appointmentConsent: persistedConsent ?? null,
  });
  mockLeadStore.set(lead.id, lead);
  activeMockLeadId = lead.id;
  return lead;
}

function makePublicLead(lead: MockLead): PublicLead {
  const appointment =
    lead.providerId !== undefined && lead.startTime !== undefined
      ? {
          ...makeBookedAppointmentProjection(
            lead.id,
            lead.providerId,
            lead.startTime,
          ).publicLeadAppointment,
          id: null,
          joinUrl: null,
        }
      : null;
  // ponytail: booked truth comes from mockRecoveryState, applied by applyRecovery callers
  const payment =
    lead.selectedMembershipPlanId === undefined && lead.cardOnFile !== true
      ? null
      : {
          selectedMembershipPlanId: lead.selectedMembershipPlanId ?? null,
          cardOnFile: lead.cardOnFile === true,
          paid: false,
        };

  return {
    firstName: lead.firstName,
    lastName: lead.lastName,
    email: lead.email,
    emailReadOnly: false,
    phone: lead.phone === undefined ? null : maskPhone(lead.phone),
    stateCode: lead.state ?? null,
    reasonForVisitCode: lead.reasonForVisitCode,
    sponsorCode: null,
    dateOfBirthOnFile: lead.dateOfBirthOnFile,
    referralSourceOnFile: lead.referralSourceOnFile,
    consentToContactViaSms: lead.consentToContactViaSms ?? null,
    appointmentConsent: lead.appointmentConsent ?? null,
    heightWeight: null,
    insurance: null,
    appointment,
    payment,
    bookingStatus: 'NOT_BOOKED',
  };
}

export function getInsuranceCompanies(): Task<
  InsuranceCompany[],
  DtiPublicApiError
> {
  // Clearable at call time so a browser scenario can exercise the real retry:
  // the spec sets the key before boot, then removes it before clicking retry.
  if (
    typeof window !== 'undefined' &&
    window.sessionStorage.getItem(MOCK_INSURANCE_FAILURE_STORAGE_KEY) === 'true'
  ) {
    return delayedResult(
      err(
        new DtiPublicApiError(
          'Mock insurance list failure',
          'transport',
          'GetInsuranceCompanies',
        ),
      ),
    );
  }
  return delayedResult(ok(mockInsurances));
}

export function searchProviders(
  params: SearchProvidersQuery,
): Task<SearchProvidersResult, DtiPublicApiError> {
  // Clearable at call time so a browser scenario can exercise the real retry:
  // the spec sets the key before boot, then removes it before clicking retry.
  if (
    typeof window !== 'undefined' &&
    window.sessionStorage.getItem(MOCK_SEARCH_FAILURE_STORAGE_KEY) === 'true'
  ) {
    return delayedResult(
      err(
        new DtiPublicApiError(
          'Mock provider search failure',
          'transport',
          'SearchProviders',
        ),
      ),
    );
  }
  const isDenseAvailabilityFixture =
    params.search?.toLowerCase() === MOCK_SEARCH_DENSE_AVAILABILITY_QUERY;
  const isAvailabilityFailedFixture =
    params.search?.toLowerCase() === MOCK_SEARCH_AVAILABILITY_FAILED_QUERY;
  const isEmptyWindowFixture = params.search?.toLowerCase() === 'empty-window';
  const normalizedState = widenUsStateCode(params.state);
  const baseFiltered = mockProviders.filter((provider) => {
    if (
      params.sponsorCode === 'closeknit' &&
      CLOSEKNIT_ALLOWED_PROVIDER_IDS[provider.id] !== true
    ) {
      return false;
    }
    if (!provider.states.includes(normalizedState)) return false;
    if (params.counselingStyle) {
      const requestedCounselingStyles =
        params.counselingStyle.tag === 'single'
          ? [params.counselingStyle.code]
          : params.counselingStyle.codes;

      if (
        !requestedCounselingStyles.some((requestedCode) =>
          provider.counselingStyles.some((item) => item.code === requestedCode),
        )
      ) {
        return false;
      }
    }
    if (
      params.search &&
      !isAvailabilityFailedFixture &&
      !isEmptyWindowFixture &&
      !isDenseAvailabilityFixture
    ) {
      const searchTerm = params.search.toLowerCase();
      const searchTarget = `${provider.firstName ?? ''} ${
        provider.lastName ?? ''
      } ${provider.bio ?? ''}`.toLowerCase();
      if (!searchTarget.includes(searchTerm)) return false;
    }
    return true;
  });

  const nextAvailabilityWindowStart = unsafeParse(
    isoTimestampSchema,
    '2026-08-24T04:00:00.000Z',
  );
  if (isEmptyWindowFixture && params.availabilityDate === undefined) {
    return delayedResult(
      ok({
        providers: [],
        total: 0,
        page: 1,
        perPage: 2,
        broadened: false,
        availabilityFetchStatus: 'SUCCESS',
        earliestAvailableSlot: null,
        availabilityWindowStart: unsafeParse(
          isoTimestampSchema,
          '2026-08-10T04:00:00.000Z',
        ),
        availabilityWindowEnd: unsafeParse(
          isoTimestampSchema,
          '2026-08-24T03:59:59.999Z',
        ),
        nextAvailabilityWindowStart,
        hasNextAvailabilityWindow: true,
      }),
    );
  }
  let filtered = baseFiltered;
  let broadened = false;
  const reasonForVisit = params.reasonForVisit;
  if (reasonForVisit?.tag === 'single') {
    filtered = baseFiltered.filter((provider) =>
      provider.reasonsForVisit.includes(reasonForVisit.code),
    );
    broadened = false;
  } else if (reasonForVisit?.tag === 'multiple') {
    const preferred = baseFiltered.filter((provider) =>
      reasonForVisit.codes.some((requestedCode) =>
        provider.reasonsForVisit.includes(requestedCode),
      ),
    );
    filtered = preferred.length === 0 ? baseFiltered : preferred;
    broadened = preferred.length === 0;
  }

  if (isDenseAvailabilityFixture) {
    // Anchored to a UTC day start so the twelve slots always land on one
    // calendar day for a UTC-timezone browser scenario.
    const denseDayStart = DateTime.now()
      .setZone('UTC')
      .startOf('day')
      .plus({ days: 2 })
      .set({ hour: 9 });
    const denseSlots = Array.from({ length: 12 }, (_, index) => ({
      startTime: unsafeParse(
        isoTimestampSchema,
        denseDayStart.plus({ minutes: index * 30 }).toISO()!,
      ),
    }));
    filtered = filtered.map((provider) => ({
      ...provider,
      availabilityPreview: { slots: denseSlots },
      nextAvailableSlot: denseSlots[0]!.startTime,
    }));
  }

  if (params.dayOfWeek !== undefined) {
    const weekdayFormatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      timeZone: params.timezone ?? 'UTC',
    });
    filtered = filtered.flatMap((provider) => {
      const availabilityPreview = provider.availabilityPreview;
      if (availabilityPreview === null) return [];

      const slots = availabilityPreview.slots.filter(
        (slot) =>
          weekdayFormatter.format(new Date(slot.startTime)).toUpperCase() ===
          params.dayOfWeek,
      );
      const nextAvailableSlot = slots[0]?.startTime;
      if (nextAvailableSlot === undefined) return [];

      return [
        {
          ...provider,
          availabilityPreview: { ...availabilityPreview, slots },
          nextAvailableSlot,
        },
      ];
    });
  }

  const MOCK_PAGE_SIZE = 2;
  const pg = params.page ?? 1;
  const pageStart = (pg - 1) * MOCK_PAGE_SIZE;
  const pagedProviders = filtered.slice(pageStart, pageStart + MOCK_PAGE_SIZE);
  const responseProviders = isAvailabilityFailedFixture
    ? pagedProviders.map((provider) => ({
        ...provider,
        availabilityPreview: null,
        nextAvailableDate: null,
      }))
    : pagedProviders;

  return delayedResult(
    ok({
      providers: responseProviders,
      total: filtered.length,
      page: pg,
      perPage: MOCK_PAGE_SIZE,
      broadened,
      availabilityFetchStatus: isAvailabilityFailedFixture
        ? 'FAILED'
        : 'SUCCESS',
      earliestAvailableSlot: filtered[0]?.nextAvailableSlot ?? null,
      availabilityWindowStart:
        filtered.length === 0
          ? null
          : unsafeParse(isoTimestampSchema, '2026-08-10T04:00:00.000Z'),
      availabilityWindowEnd:
        filtered.length === 0
          ? null
          : unsafeParse(isoTimestampSchema, '2026-08-24T03:59:59.999Z'),
      nextAvailabilityWindowStart:
        filtered.length === 0
          ? null
          : unsafeParse(isoTimestampSchema, '2026-08-24T04:00:00.000Z'),
      hasNextAvailabilityWindow: filtered.length !== 0,
    }),
  );
}

export function fetchProviderAvailability(
  providerId: ProviderId,
  intervalStart?: IsoTimestamp,
  options?: { timezone?: BrowserTimezone; signal?: AbortSignal },
): Task<ProviderAvailabilityResult, DtiPublicApiError> {
  const isPagedProvider = providerId === PAGED_AVAILABILITY_PROVIDER_ID;
  const timezone = options?.timezone ?? 'UTC';
  const requestedIntervalStart =
    intervalStart === undefined
      ? DateTime.now().setZone(timezone).toJSDate()
      : new Date(intervalStart);
  const localIntervalStart = DateTime.fromJSDate(requestedIntervalStart, {
    zone: timezone,
  }).startOf('day');
  const isEmptyPage =
    isPagedProvider &&
    intervalStart !== undefined &&
    requestedIntervalStart.getTime() >=
      new Date(PAGED_AVAILABILITY_CURSOR).getTime() &&
    requestedIntervalStart.getTime() <
      new Date(PAGED_AVAILABILITY_NEXT_CURSOR).getTime();
  const isTerminalPage =
    isPagedProvider &&
    intervalStart !== undefined &&
    requestedIntervalStart.getTime() >=
      new Date(PAGED_AVAILABILITY_NEXT_CURSOR).getTime();
  const availabilities = isPagedProvider
    ? isEmptyPage
      ? []
      : (isTerminalPage ? [1, 3, 5] : [1, 3, 5, 7]).flatMap((startDay) =>
          makeAppointmentSlotsFrom(localIntervalStart, startDay, timezone),
        )
    : makeOrdinaryAvailability(
        localIntervalStart,
        providerId,
        timezone,
        intervalStart === undefined ? 1 : 0,
      );
  const intervalStartUtc = localIntervalStart.toUTC();
  const intervalEndUtc = localIntervalStart.plus({ days: 14 }).toUTC();
  return delayedResult(
    ok({
      tag: 'available',
      providerId: unsafeParse(providerIdSchema, providerId),
      availabilities,
      intervalStart: unsafeParse(isoTimestampSchema, intervalStartUtc.toISO()!),
      intervalEnd: unsafeParse(isoTimestampSchema, intervalEndUtc.toISO()!),
      nextIntervalStart:
        isPagedProvider && !isTerminalPage
          ? isEmptyPage
            ? PAGED_AVAILABILITY_NEXT_CURSOR
            : PAGED_AVAILABILITY_CURSOR
          : null,
      hasNextInterval: isPagedProvider && !isTerminalPage,
      errorCode: null,
    }),
    options?.signal === undefined
      ? undefined
      : {
          signal: options.signal,
          abortError: new DtiPublicApiError(
            'The operation was aborted',
            'aborted',
            'ProviderAvailability',
          ),
        },
  );
}

export function createLead(
  command: CreateLeadCommand,
): Task<CreateLeadResult, DtiPublicApiError> {
  const normalizedEmail = command.email.toLowerCase();
  const input = command.input;
  const ageIneligible = isMockAgeIneligibleDateOfBirth(input?.dateOfBirth);
  if (!ageIneligible && normalizedEmail === MOCK_UPDATE_LEAD_SENTINEL_EMAIL) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [
          {
            field: ['email'],
            messages: ['UpdateLead sentinel must not create'],
            code: null,
          },
        ],
        lead: null,
      }),
    );
  }

  if (
    !ageIneligible &&
    normalizedEmail === MOCK_CREATE_LEAD_FIELD_ERROR_EMAIL
  ) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [
          {
            field: ['email'],
            messages: ['Email is already taken'],
            code: null,
          },
        ],
        lead: null,
      }),
    );
  }

  if (
    !ageIneligible &&
    normalizedEmail === MOCK_CREATE_LEAD_GENERAL_ERROR_EMAIL
  ) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [
          {
            field: ['base'],
            messages: ['Something went wrong on our end'],
            code: null,
          },
        ],
        lead: null,
      }),
    );
  }

  if (
    !ageIneligible &&
    normalizedEmail === MOCK_CREATE_LEAD_NETWORK_ERROR_EMAIL
  ) {
    return delayedResult(
      err(
        new DtiPublicApiError(
          'Mock CreateLead network failure',
          'transport',
          'CreateLead',
        ),
      ),
    );
  }

  const persistedActiveLeadId = readMockRecoveryStorage()?.activeLeadId ?? null;
  if (persistedActiveLeadId !== null) {
    const numericId = Number(
      String(persistedActiveLeadId).replace('lead-', ''),
    );
    if (Number.isSafeInteger(numericId)) {
      mockLeadCounter = Math.max(mockLeadCounter, numericId);
    }
  }

  const lead = makeMockLead({
    email: maskEmail(command.email),
    phone: input?.phone,
    firstName: parseOptionalFirstName(input?.firstName),
    lastName: parseOptionalLastName(input?.lastName),
    reasonForVisitCode:
      input?.reasonForVisitCode === undefined
        ? null
        : unsafeParse(reasonForVisitCodeSchema, input.reasonForVisitCode),
    providerId: input?.appointment?.providerId,
    startTime: input?.appointment?.startTime,
    state: input?.stateCd,
    dateOfBirthOnFile: input?.dateOfBirth !== undefined,
    ...(ageIneligible ? { ageIneligible: true } : {}),
    referralSourceOnFile: input?.referralSource !== undefined,
    consentToContactViaSms: input?.smsConsent ?? null,
    appointmentConsent: input?.appointmentConsent ?? null,
    ...(command.email.toLowerCase() ===
    MOCK_BOOK_APPOINTMENT_PROVIDER_REJECTED_EMAIL
      ? { rejectsAppointmentProvider: true }
      : {}),
  });
  mockLeadStore.set(lead.id, lead);
  activeMockLeadId = lead.id;
  const recovery = getOrCreateRecovery(lead.id);
  if (input?.insurance !== undefined) {
    recovery.insurance = projectLeadInsurance(
      input.insurance,
      recovery.insurance,
      false,
    ).insurance;
  }
  const magicLinkSent =
    normalizedEmail === MOCK_CREATE_LEAD_MAGIC_LINK_SENT_EMAIL;
  if (magicLinkSent && !ageIneligible) {
    reissueMagicLink();
  }
  persistMockRecoveryState();

  const result: CreateLeadResult = ageIneligible
    ? {
        tag: 'validation_error',
        errors: [mockAgeIneligibleError()],
        lead: makePublicLead(lead),
      }
    : magicLinkSent
      ? {
          tag: 'magic_link_sent',
          lead: null,
          email: command.email,
          magicLinkReason: 'IDENTITY_CHECK',
        }
      : {
          tag: 'created',
          lead: makePublicLead(lead),
        };

  return delayedResult(ok(result));
}

export function updateLead(
  command: UpdateLeadCommand,
): Task<UpdateLeadResult, DtiPublicApiError> {
  const leadId = activeMockLeadId;
  if (leadId === null) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [
          { field: ['leadId'], messages: ['Lead not found'], code: null },
        ],
        lead: null,
      }),
    );
  }
  const existing = mockLeadStore.get(leadId);
  if (!existing) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [
          { field: ['leadId'], messages: ['Lead not found'], code: null },
        ],
        lead: null,
      }),
    );
  }

  const ageIneligible =
    command.input.dateOfBirth === undefined
      ? existing.ageIneligible === true
      : isMockAgeIneligibleDateOfBirth(command.input.dateOfBirth);

  const updated: MockLead = {
    ...existing,
    email:
      command.email === undefined ? existing.email : maskEmail(command.email),
    phone: command.input.phone ?? existing.phone,
    firstName:
      parseOptionalFirstName(command.input.firstName) ?? existing.firstName,
    lastName:
      parseOptionalLastName(command.input.lastName) ?? existing.lastName,
    reasonForVisitCode:
      command.input.reasonForVisitCode === undefined
        ? existing.reasonForVisitCode
        : unsafeParse(
            reasonForVisitCodeSchema,
            command.input.reasonForVisitCode,
          ),
    providerId: command.input.appointment?.providerId ?? existing.providerId,
    startTime: command.input.appointment?.startTime ?? existing.startTime,
    state: command.input.stateCd ?? existing.state,
    dateOfBirthOnFile:
      command.input.dateOfBirth === undefined
        ? existing.dateOfBirthOnFile
        : true,
    referralSourceOnFile:
      command.input.referralSource === undefined
        ? existing.referralSourceOnFile
        : true,
    ageIneligible: ageIneligible ? true : undefined,
    consentToContactViaSms:
      command.input.smsConsent === undefined
        ? (existing.consentToContactViaSms ?? null)
        : command.input.smsConsent,
    appointmentConsent:
      command.input.appointmentConsent === undefined
        ? (existing.appointmentConsent ?? null)
        : command.input.appointmentConsent,
  };
  mockLeadStore.set(leadId, updated);
  persistMockRecoveryState();

  return delayedResult(
    ok(
      ageIneligible
        ? {
            tag: 'validation_error',
            errors: [mockAgeIneligibleError()],
            lead: makePublicLead(updated),
          }
        : {
            tag: 'updated',
            lead: makePublicLead(updated),
          },
    ),
  );
}

// E2E test bridge: the pre-booking UI no longer exposes an out-of-network
// carrier path, so a browser test cannot organically drive a SubmitInsurance
// into OUT_OF_NETWORK. This one-shot latch lets a Playwright spec force the
// next submission's coverage outcome to out-of-network, surfacing the
// pre-booking self-pay offer. Mock-only; inert on the real-API path.
let forceOutOfNetworkOnNextSubmit = false;

function seedMockPreBookingOutOfNetwork(): void {
  forceOutOfNetworkOnNextSubmit = true;
}

type MockLeadInsurance = NonNullable<PublicLead['insurance']>;
type LeadInsuranceProjection = Readonly<{
  insurance: MockLeadInsurance &
    Readonly<{ selection: NonNullable<MockLeadInsurance['selection']> }>;
  eligibilityStatus: EligibilityStatus | null;
}>;

function projectLeadInsurance(
  insuranceInput: InsuranceSelectionInput | undefined,
  recoveryInsurance: MockLeadInsurance | null,
  forceOutOfNetwork: boolean,
): LeadInsuranceProjection {
  const selection = toLeadInsuranceSelectionPayload(insuranceInput);
  const eligibilityStatus = determineEligibilityStatus(
    insuranceInput?.memberId,
  );
  const insuranceCompany =
    selection?.type === 'SPONSOR'
      ? mockInsurances.find((company) => company.id === selection.sponsorId)
      : undefined;
  const companyName =
    selection?.type === 'OTHER'
      ? selection.otherCompanyName === undefined
        ? null
        : unsafeParse(insuranceCompanyNameSchema, selection.otherCompanyName)
      : insuranceCompany === undefined
        ? null
        : unsafeParse(insuranceCompanyNameSchema, insuranceCompany.name);
  const otherCompanyName =
    selection?.type === 'OTHER'
      ? selection.otherCompanyName === undefined
        ? null
        : unsafeParse(otherCompanyNameSchema, selection.otherCompanyName)
      : null;
  const isSponsorSelection = selection?.type === 'SPONSOR';
  return {
    insurance: {
      selection:
        selection?.type === 'SPONSOR'
          ? {
              type: 'SPONSOR',
              networkStatus: forceOutOfNetwork
                ? 'OUT_OF_NETWORK'
                : 'IN_NETWORK',
              sponsorId: unsafeParse(
                insuranceCompanyIdSchema,
                selection.sponsorId,
              ),
              otherCompanyName: null,
            }
          : selection?.type === 'OTHER'
            ? {
                type: 'OTHER',
                networkStatus: 'OUT_OF_NETWORK',
                sponsorId: null,
                otherCompanyName,
              }
            : {
                type: 'SELF_PAY',
                networkStatus: 'SELF_PAY',
                sponsorId: null,
                otherCompanyName: null,
              },
      details: isSponsorSelection
        ? {
            memberIdOnFile: insuranceInput?.memberId !== undefined,
            groupNumberOnFile: insuranceInput?.groupId !== undefined,
            subscriberRelationship:
              insuranceInput?.subscriber?.relationship ?? null,
            policyholderOnFile:
              insuranceInput?.subscriber?.relationship !== undefined,
          }
        : null,
      eligibilityStatus,
      cards: recoveryInsurance?.cards ?? null,
      companyName,
    },
    eligibilityStatus,
  };
}

export function submitInsurance(
  command: SubmitInsuranceCommand,
): Task<SubmitInsuranceResult, DtiPublicApiError> {
  const forceOutOfNetwork = forceOutOfNetworkOnNextSubmit;
  forceOutOfNetworkOnNextSubmit = false;

  const existing = findActiveMockLead();
  if (!existing) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [
          { field: ['leadId'], messages: ['Lead not found'], code: null },
        ],
        lead: null,
      }),
    );
  }

  if (
    command.insurance?.memberId ===
    MOCK_SUBMIT_INSURANCE_POLICYHOLDER_ERROR_MEMBER_ID
  ) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [
          {
            field: ['details', 'policyholder'],
            messages: ['Policyholder information could not be verified.'],
            code: null,
          },
        ],
        lead: makePublicLead(existing),
      }),
    );
  }

  const recovery = getOrCreateRecovery(existing.id);
  const { insurance, eligibilityStatus } = projectLeadInsurance(
    command.insurance,
    recovery.insurance,
    forceOutOfNetwork,
  );
  const leadWithInsurance: PublicLead = {
    ...makePublicLead(existing),
    insurance,
  };
  recovery.insurance = insurance;
  persistMockRecoveryState();

  return delayedResult(
    ok({
      tag: 'submitted',
      lead: leadWithInsurance,
      insuranceStatus: {
        status: insurance.selection.networkStatus,
        insuranceCompanyName: insurance.companyName,
      },
      eligibilityStatus,
    }),
  );
}

export function bookAppointment(
  command: BookAppointmentCommand,
): Task<BookAppointmentResult, DtiPublicApiError> {
  const existing = findActiveMockLead();
  if (!existing) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [
          { field: ['leadId'], messages: ['Lead not found'], code: null },
        ],
        errorCode: 'VALIDATION_FAILED',
        retryable: false,
        lead: null,
      }),
    );
  }

  const providerId =
    command.appointment?.providerId ??
    (existing.providerId as ProviderSummary['id']);
  const startTime = command.appointment?.startTime ?? existing.startTime;
  if (!providerId || !startTime) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [
          {
            field: ['base'],
            messages: ['Missing booking details'],
            code: null,
          },
        ],
        errorCode: 'VALIDATION_FAILED',
        retryable: false,
        lead: makePublicLead(existing),
      }),
    );
  }
  if (existing.ageIneligible === true) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [mockAgeIneligibleError()],
        errorCode: 'VALIDATION_FAILED',
        retryable: false,
        lead: makePublicLead(existing),
      }),
    );
  }

  if (existing.rejectsAppointmentProvider) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [
          {
            field: ['appointment', 'providerId'],
            messages: ['This provider is no longer available.'],
            code: null,
          },
        ],
        errorCode: 'VALIDATION_FAILED',
        retryable: false,
        lead: makePublicLead(existing),
      }),
    );
  }

  if (shouldFailMockBookingForStartTime(String(startTime))) {
    return delayedResult(
      ok({
        tag: 'retryable_failure',
        errors: [],
        errorCode: 'SCHEDULING_TIMEOUT',
        retryable: true,
        lead: makePublicLead(existing),
      }),
    );
  }

  const updatedLead: MockLead = {
    ...existing,
    providerId,
    startTime,
  };
  mockLeadStore.set(updatedLead.id, updatedLead);

  const { appointment, publicLeadAppointment } =
    makeBookedAppointmentProjection(updatedLead.id, providerId, startTime);

  const bookedLead: PublicLead = {
    ...makePublicLead(updatedLead),
    appointment: publicLeadAppointment,
  };

  getOrCreateRecovery(updatedLead.id).appointment = bookedLead.appointment;
  persistMockRecoveryState();

  if (String(startTime).endsWith('11:00:00.000Z')) {
    return delayedResult(
      ok({
        tag: 'already_booked',
        errors: [
          {
            field: ['base'],
            messages: ['Appointment already exists.'],
            code: null,
          },
        ],
        errorCode: 'ALREADY_BOOKED',
        retryable: false,
        lead: bookedLead,
      }),
    );
  }

  return delayedResult(
    ok({
      tag: 'booked',
      lead: bookedLead,
      appointment,
      payment: null,
      errorCode: null,
      retryable: false,
    }),
  );
}
function applyRecovery(publicLead: PublicLead, leadId: LeadId): PublicLead {
  const recovery = getOrCreateRecovery(leadId);
  return {
    ...publicLead,
    ...(recovery.appointment !== null && { appointment: recovery.appointment }),
    ...(recovery.insurance !== null && { insurance: recovery.insurance }),
    ...(recovery.appointment !== null && { bookingStatus: 'CONFIRMED' }),
  };
}

export function fetchPublicLead(): Task<PublicLead, DtiPublicApiError> {
  const lead = ensureActiveMockLead();
  return delayedResult(ok(applyRecovery(makePublicLead(lead), lead.id)));
}

export function consumeMagicLink(
  command: ConsumeMagicLinkCommand,
): Task<ConsumeMagicLinkResult, DtiPublicApiError> {
  if (command.token === MOCK_BOOKED_RECOVERY_TOKEN) {
    seedMockBookedRecovery();
  }
  if (command.token === MOCK_SELF_PAY_NEEDS_PLAN_TOKEN) {
    const lead = ensureActiveMockLead();
    lead.email = null;
    lead.firstName = null;
    lead.lastName = null;
    lead.phone = undefined;
    mockRecoveryState.set(lead.id, {
      appointment: null,
      insurance: {
        selection: {
          type: 'SELF_PAY',
          networkStatus: 'SELF_PAY',
          sponsorId: null,
          otherCompanyName: null,
        },
        details: null,
        eligibilityStatus: null,
        cards: null,
        companyName: null,
      },
    });
    persistMockRecoveryState();
    const selfPayLead = makePublicLead(lead);
    return delayedResult(
      ok({
        tag: 'consumed',
        lead: {
          ...selfPayLead,
          firstName: null,
          lastName: null,
          email: null,
          phone: null,
          insurance: {
            selection: {
              type: 'SELF_PAY',
              networkStatus: 'SELF_PAY',
              sponsorId: null,
              otherCompanyName: null,
            },
            details: null,
            eligibilityStatus: null,
            cards: null,
            companyName: null,
          },
          appointment: null,
          payment: null,
          bookingStatus: 'NOT_BOOKED',
        },
      }),
    );
  }
  const lead = ensureActiveMockLead();
  const isIssuedToken = command.token === MOCK_ISSUED_MAGIC_LINK_TOKEN;
  const consumedOnEarlierLoad = isIssuedToken && isIssuedMagicLinkConsumed();
  if (consumedOnEarlierLoad && !mockIssuedMagicLinkConsumeInFlight) {
    return delayedResult(ok(mockIssuedMagicLinkExpiredResult));
  }
  const tokenResult =
    command.token === MOCK_ALREADY_BOOKED_TOKEN
      ? { tag: 'already_booked' as const, errors: [] }
      : null;
  const outcome = tokenResult ??
    mockMagicLinkConsumeOverride ?? {
      tag: 'consumed',
      lead: applyRecovery(makePublicLead(lead), lead.id),
    };
  const resultTask = delayedResult(ok(outcome));
  if (!isIssuedToken) {
    return resultTask;
  }
  if (!consumedOnEarlierLoad) {
    mockIssuedMagicLinkConsumed = true;
    mockIssuedMagicLinkConsumeInFlight = true;
    persistMockRecoveryState();
  }
  return async () => {
    try {
      return await resultTask();
    } finally {
      mockIssuedMagicLinkConsumeInFlight = false;
    }
  };
}

export function consumeLeadInvite(
  command: ConsumeLeadInviteCommand,
): Task<ConsumeLeadInviteResult, DtiPublicApiError> {
  const lead = ensureActiveMockLead();
  const tokenResult =
    command.token === MOCK_EXPIRED_INVITE_TOKEN
      ? {
          tag: 'validation_error' as const,
          lead: null,
          errors: [
            {
              field: ['token'],
              messages: ['Invalid or expired token'],
              code: null,
            },
          ],
        }
      : command.token === MOCK_TEMPORARY_FAILURE_INVITE_TOKEN
        ? {
            tag: 'failed' as const,
            lead: null,
            errors: [
              {
                field: ['base'],
                messages: ['Temporary recovery failure'],
                code: null,
              },
            ],
          }
        : null;
  return delayedResult(
    ok(
      tokenResult ??
        mockLeadInviteConsumeOverride ?? {
          tag: 'consumed',
          lead: applyRecovery(makePublicLead(lead), lead.id),
        },
    ),
  );
}

export function requestMagicLink(
  _command: RequestMagicLinkCommand,
): Task<RequestMagicLinkResult, DtiPublicApiError> {
  reissueMagicLink();
  return delayedResult(ok({ tag: 'accepted' }));
}

export function fetchPartnerConfig(
  code: string,
): Task<PartnerConfig | null, DtiPublicApiError> {
  if (code === 'partner-error') {
    return delayedResult(
      err(
        new DtiPublicApiError(
          'partner config unavailable',
          'transport',
          'fetchPartnerConfig',
        ),
      ),
    );
  }

  return delayedResult(
    ok(
      code === 'closeknit'
        ? CLOSEKNIT_PARTNER_CONFIG
        : code === 'riverside'
          ? RIVERSIDE_PARTNER_CONFIG
          : code === 'malformed-restricted-insurer'
            ? MALFORMED_RESTRICTED_PARTNER_CONFIG
            : null,
    ),
  );
}

export function fetchDtiConfig(args: {
  variant: DtiConfigVariant;
  sponsorCode?: string;
}): Task<DtiConfig | null, DtiPublicApiError> {
  if (args.sponsorCode === 'partner-error') {
    return delayedResult(
      err(
        new DtiPublicApiError(
          'dti config unavailable',
          'transport',
          'fetchDtiConfig',
        ),
      ),
    );
  }

  if (args.sponsorCode === 'null-dti-config') {
    return delayedResult(ok(null));
  }

  return delayedResult(
    ok(
      unsafeParse(dtiConfigResponseSchema, {
        dtiConfig: {
          variant: args.variant,
          steps: [MOCK_BOOK_APPOINTMENT_STEP],
        },
      }).dtiConfig,
    ),
  );
}

export function fetchMembershipPackages(): Task<
  MembershipPackage[],
  DtiPublicApiError
> {
  return delayedResult(ok(mockMembershipPackages));
}

export function fetchReferenceData(): Task<ReferenceData, DtiPublicApiError> {
  return delayedResult(
    ok({
      insuranceCompanies: mockInsurances,
      reasonsForVisit: mockReasonsForVisit,
      membershipPackages: mockMembershipPackages,
    }),
  );
}

export function fetchSearchProviderFilters(options?: {
  signal?: AbortSignal;
}): Task<SearchProviderFilters, DtiPublicApiError> {
  return delayedResult(
    ok(
      searchProviderFiltersResponseSchema.parse({
        searchProviderFilters: {
          ageGroups: [
            {
              code: 'AG_YOUNG_CHILDREN',
              name: 'Young Children',
              label: 'Young Children (0-5 years)',
            },
            {
              code: 'AG_ADOLESCENT',
              name: 'Adolescent',
              label: 'Adolescent (6-11 years)',
            },
            {
              code: 'AG_EARLY_TEEN',
              name: 'Early Teen',
              label: 'Early Teen (12-15 years)',
            },
            {
              code: 'AG_LATE_TEEN',
              name: 'Late Teen',
              label: 'Late Teen (16-17 years)',
            },
            { code: 'AG_ADULT', name: 'Adult', label: 'Adult (18+ years)' },
          ],
          counselingStyles: [
            { code: 'COLLABORATIVE', name: 'Gentle / Collaborative' },
            { code: 'PRESCRIPTIVE', name: 'Direct / Prescriptive' },
          ],
          // ponytail: curated filter vocabulary, not the full mockReasonsForVisit
          // taxonomy — the details filter deliberately omits RFV_ANY/RFV_OTHER.
          reasonsForVisit: [
            { code: 'RFV_SUSTAINABLE_WEIGHT_LOSS', name: 'Weight Management' },
            { code: 'RFV_TYPE_2_DIABETES', name: 'Type 2 Diabetes' },
            { code: 'RFV_PREDIABETES', name: 'Prediabetes' },
            { code: 'RFV_TYPE_1_DIABETES', name: 'Type 1 Diabetes' },
            { code: 'RFV_GESTATIONAL_DIABETES', name: 'Gestational Diabetes' },
            {
              code: 'RFV_GASTROINTESTINAL_HEALTH',
              name: 'Gastrointestinal Health',
            },
            { code: 'RFV_HEART_HEALTH', name: 'Heart Health' },
            { code: 'RFV_HORMONE_HEALTH', name: 'Hormone Health' },
            { code: 'RFV_EATING_DISORDERS', name: 'Eating Disorders' },
          ],
        },
      }).searchProviderFilters,
    ),
    {
      signal: options?.signal,
      abortError: new DtiPublicApiError(
        'The operation was aborted',
        'aborted',
        'SearchProviderFilters',
      ),
    },
  );
}

export function selectMembershipPlan(
  command: SelectMembershipPlanCommand,
): Task<SelectMembershipPlanResult, DtiPublicApiError> {
  const lead = ensureActiveMockLead();
  const updated: MockLead = {
    ...lead,
    selectedMembershipPlanId: command.planId,
  };
  mockLeadStore.set(updated.id, updated);
  getOrCreateRecovery(updated.id);
  persistMockRecoveryState();
  return delayedResult(
    ok({
      tag: 'selected',
      lead: makePublicLead(updated),
      selectedMembershipPlanId: command.planId,
    }),
  );
}

export function savePaymentMethod(
  _command: SavePaymentMethodCommand,
): Task<SavePaymentMethodResult, DtiPublicApiError> {
  const lead = ensureActiveMockLead();
  const updated: MockLead = { ...lead, cardOnFile: true };
  mockLeadStore.set(updated.id, updated);
  return delayedResult(
    ok({
      tag: 'saved',
      lead: makePublicLead(updated),
      payment: {
        paid: false,
        cardOnFile: true,
      },
    }),
  );
}

export const MOCK_PROMO_FULL_DISCOUNT_NO_CARD_CODE = 'FULLDISC';

export function validatePromoCode(
  command: ValidatePromoCodeCommand,
): Task<ValidatePromoCodeResult, DtiPublicApiError> {
  if (command.code === MOCK_PROMO_REJECTED_CODE) {
    return delayedResult(
      ok({
        tag: 'validation_error',
        errors: [
          {
            field: ['code'],
            messages: ['This promo code has already been used.'],
            code: null,
          },
        ],
      }),
    );
  }
  if (command.code === MOCK_PROMO_FULL_DISCOUNT_NO_CARD_CODE) {
    return delayedResult(
      ok({
        tag: 'validated',
        discountedPriceCents: 0,
        newRecurringPriceCents: null,
        repeatTimes: null,
        couponCode: unsafeParse(promoCodeSchema, command.code),
        stillRequiresCreditCard: false,
      }),
    );
  }

  return delayedResult(
    ok({
      tag: 'validated',
      discountedPriceCents: 8900,
      newRecurringPriceCents: 8900,
      repeatTimes: 1,
      couponCode: unsafeParse(promoCodeSchema, command.code),
      stillRequiresCreditCard: true,
    }),
  );
}

export function uploadInsuranceCard(
  command: UploadInsuranceCardCommand,
): Task<UploadInsuranceCardResult, DtiPublicApiError> {
  const lead = ensureActiveMockLead();
  const recovery = getOrCreateRecovery(lead.id);
  const insurance = recovery.insurance ?? {
    selection: null,
    details: null,
    eligibilityStatus: null,
    cards: null,
    companyName: null,
  };
  const cards = insurance.cards ?? {
    front: { uploaded: false },
    back: { uploaded: false },
  };
  recovery.insurance = {
    ...insurance,
    cards: {
      ...cards,
      [command.side === 'FRONT' ? 'front' : 'back']: { uploaded: true },
    },
  };
  persistMockRecoveryState();

  return delayedResult(
    ok({
      tag: 'uploaded',
      lead: applyRecovery(makePublicLead(lead), lead.id),
      fileAttachmentId: unsafeParse(
        fileAttachmentIdSchema,
        'mock-file-attachment',
      ),
    }),
  );
}

function determineEligibilityStatus(
  memberId: string | undefined,
): EligibilityStatus | null {
  if (!memberId) return null;
  // No trim: e2e specs rely on the sentinel seeing exactly what the client submitted.
  const normalized = memberId.toUpperCase();
  if (normalized.endsWith('0')) return 'INACTIVE';
  if (normalized.endsWith('5')) return 'NOT_FOUND';
  if (normalized.endsWith('9')) return 'TIMEOUT';
  return 'ACTIVE';
}
// ponytail: E2E test bridge — remove when real API replaces mocks
declare global {
  interface Window {
    __seedMockAlreadyBookedRecovery: typeof seedMockAlreadyBookedRecovery;
    __seedMockBookedRecovery: typeof seedMockBookedRecovery;
    __seedMockExpiredInviteRecovery: typeof seedMockExpiredInviteRecovery;
    __seedMockExpiredTokenRecovery: typeof seedMockExpiredTokenRecovery;
    __seedMockPendingRecovery: typeof seedMockPendingRecovery;
    __seedMockOtherCarrierRecovery: typeof seedMockOtherCarrierRecovery;
    __seedMockPreBookingOutOfNetwork: typeof seedMockPreBookingOutOfNetwork;
  }
}

if (typeof window !== 'undefined') {
  window.__seedMockAlreadyBookedRecovery = seedMockAlreadyBookedRecovery;
  window.__seedMockBookedRecovery = seedMockBookedRecovery;
  window.__seedMockExpiredInviteRecovery = seedMockExpiredInviteRecovery;
  window.__seedMockExpiredTokenRecovery = seedMockExpiredTokenRecovery;
  window.__seedMockPendingRecovery = seedMockPendingRecovery;
  window.__seedMockOtherCarrierRecovery = seedMockOtherCarrierRecovery;
  window.__seedMockPreBookingOutOfNetwork = seedMockPreBookingOutOfNetwork;
}
